import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getConversation, type AdminConversationDetail } from "@admin/lib/api";
import { formatDate } from "@admin/lib/utils";
import {
  formatLatencyMs,
  formatMicroUsd,
  parseToolCallsJson,
  roleLabel,
} from "@admin/lib/conversation-display";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { Button } from "@admin/components/ui/button";
import { cn } from "@admin/lib/utils";

function ToolBlock({ toolCallsJson, toolName }: { toolCallsJson: string | null; toolName: string | null }) {
  const [open, setOpen] = useState(false);
  const parsed = parseToolCallsJson(toolCallsJson);
  if (!parsed && !toolName) return null;
  return (
    <div className="mt-2 rounded border border-border bg-muted/20 p-2 text-xs">
      <button
        type="button"
        className="font-medium text-muted-fg hover:text-fg"
        onClick={() => setOpen((v) => !v)}
      >
        {toolName ? `Tool: ${toolName}` : "Tool calls"} {open ? "(hide)" : "(show)"}
      </button>
      {open && (
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all font-mono text-[11px]">
          {JSON.stringify(parsed ?? toolName, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function ConversationDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [data, setData] = useState<AdminConversationDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    getConversation(sessionId)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : "Load failed"));
  }, [sessionId]);

  if (err) return <p className="text-danger">{err}</p>;
  if (!data) return <p className="text-muted-fg">Loading...</p>;

  const { session, messages, attachments, composioTools } = data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="outline" size="sm">
          <Link to="/conversations">Back</Link>
        </Button>
        <h1 className="text-2xl font-semibold">{session.title ?? "Conversation"}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Session</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <span className="text-muted-fg">User: </span>
            <Link to={`/users/${session.userId}`} className="text-accent hover:underline">
              {session.userId}
            </Link>
          </div>
          <div>
            <span className="text-muted-fg">Source: </span>
            {session.source}
          </div>
          <div>
            <span className="text-muted-fg">Model: </span>
            <span className="font-mono text-xs">{session.model ?? "-"}</span>
          </div>
          <div>
            <span className="text-muted-fg">Cost: </span>
            {formatMicroUsd(session.actualCostUsdMicro)}
          </div>
          <div>
            <span className="text-muted-fg">Tokens: </span>
            {session.inputTokens} in / {session.outputTokens} out
          </div>
          <div>
            <span className="text-muted-fg">Started: </span>
            {formatDate(session.startedAt)}
          </div>
        </CardContent>
      </Card>

      {attachments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Attachments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {attachments.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2">
                <span>{a.filename}</span>
                <span className="text-xs text-muted-fg">
                  {a.direction} - {(a.sizeBytes / 1024).toFixed(1)} KB
                </span>
                {a.hasBlob ? (
                  <a
                    className="text-xs text-accent hover:underline"
                    href={`/api/conversations/${session.id}/attachments/${a.id}`}
                  >
                    Download
                  </a>
                ) : (
                  <span className="text-xs text-muted-fg">metadata only</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {composioTools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Composio tool calls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-xs font-mono">
            {composioTools.map((t) => (
              <div key={t.id}>
                {formatDate(t.ts)} - {t.toolName} ({t.outcome})
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <h2 className="text-lg font-medium">Transcript</h2>
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "rounded-lg border border-border p-3 text-sm",
              m.role === "user" ? "bg-muted/30" : "bg-card",
            )}
          >
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-fg">
              <span className="font-medium">{roleLabel(m.role)}</span>
              <span>{formatDate(m.messageAt)}</span>
              {m.turnLatencyMs != null && (
                <span>latency {formatLatencyMs(m.turnLatencyMs)}</span>
              )}
              {m.tokenCount != null && <span>{m.tokenCount} tok</span>}
            </div>
            {m.content && (
              <pre className="whitespace-pre-wrap break-words font-sans text-sm">{m.content}</pre>
            )}
            <ToolBlock toolCallsJson={m.toolCallsJson} toolName={m.toolName} />
          </div>
        ))}
      </div>
    </div>
  );
}
